// ============================================================================
// FinanceYearChart (E2, F-522) — evolution janv..dec : reel puis prevu (budget)
// ============================================================================
// Portage FIDELE du PoC components/finance/finance-year-chart.tsx : SVG maison,
// AUCUNE dependance de charting (meme philosophie que les autres graphiques du
// parc). 3 courbes $ pleines (reel, janv -> mois courant) prolongees par un troncon
// pointille « prevu » (budget, mois courant -> dec, M-508 isForecast), plus deux
// bascules comparatives pleine annee (budget / annee prec., F-522).
//
// ResizeObserver (§E du lot) : le SVG est rendu en 1:1 (unites viewBox = pixels)
// pour que le texte des mois ne soit jamais etire. 720 = defaut du 1er rendu.
//
// COULEURS (BL-12) : `--chart-cat-*` sont definies dans src/index.css. Les depenses
// prennent la MARQUE (`rgb(var(--brand-500-rgb))`) la ou le PoC prenait `--gs-red`
// (sa variable de marque, absente de gest) : la teinte suit ainsi le branding
// runtime de la compagnie (ADR-059) au lieu d'etre figee en dur.
// Adherence #28 : aucun hex ici, aucune taille litterale (echelle nommee text-chip).

import { useEffect, useRef, useState } from 'react';
import type { TFunction } from 'i18next';
import type { FinanceYearPoint } from '../../hooks/useFinance';
import { formatCad, formatMonthShort } from '../../pages/finance/format';

type SeriesKey = 'revenue' | 'expense' | 'net';
type Variant = 'actual' | 'budget' | 'priorYear';

// Indicateurs $ traces (la marge brute, en %, est volontairement exclue).
const SERIES: ReadonlyArray<{ key: SeriesKey; labelKey: string; color: string }> = [
  { key: 'revenue', labelKey: 'gest:finance.series.revenue', color: 'var(--chart-cat-5)' },
  { key: 'expense', labelKey: 'gest:finance.series.expense', color: 'rgb(var(--brand-500-rgb))' },
  { key: 'net', labelKey: 'gest:finance.series.net', color: 'var(--chart-cat-3)' },
];

const CARD = 'rounded-xl border border-slate-200 bg-white p-4 shadow-sm dark:border-slate-700 dark:bg-slate-800';

export interface FinanceYearChartProps {
  points: FinanceYearPoint[];
  locale: string;
  t: TFunction;
}

export function FinanceYearChart({ points, locale, t }: FinanceYearChartProps) {
  const [showBudget, setShowBudget] = useState(false);
  const [showPriorYear, setShowPriorYear] = useState(false);

  const containerRef = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(720);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setWidth(el.clientWidth || 720);
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const year = points[0]?.period.split('-')[0] ?? '';

  if (points.length === 0) {
    return (
      <section className={CARD}>
        <h2 className="text-head font-semibold text-slate-800 dark:text-slate-100">
          {t('gest:finance.yearChart.emptyTitle')}
        </h2>
        <p className="mt-1 text-field text-slate-500 dark:text-slate-400">
          {t('gest:finance.yearChart.empty')}
        </p>
      </section>
    );
  }

  // Hauteur responsive (~ratio 3.4:1), bornee pour rester lisible sur mobile sans
  // devenir demesuree sur grand ecran.
  const height = Math.round(Math.min(360, Math.max(190, width / 3.4)));
  const padL = 16;
  const padR = 16;
  const labelZone = 22;
  const chartH = height - labelZone;

  // Dernier indice REEL (mois courant) ; au-dela = prevu (budget).
  const lastRealIdx = points.reduce((last, p, i) => (p.isForecast ? last : i), -1);
  const hasForecast = points.some((p) => p.isForecast);

  // Bornes Y : pour chaque mois, la valeur effectivement TRACEE (reel pour les mois
  // passes, budget pour les mois prevus) + les comparatifs des bascules actives —
  // sinon les mois prevus (reel = 0) aplatiraient l'echelle.
  const values: number[] = [];
  for (const p of points) {
    for (const s of SERIES) {
      values.push(p.isForecast ? p[s.key].budget : p[s.key].actual);
      if (showBudget) values.push(p[s.key].budget);
      if (showPriorYear) values.push(p[s.key].priorYear);
    }
  }
  const top = Math.max(0, ...values);
  const bottom = Math.min(0, ...values);
  const span = top - bottom || 1;

  const innerW = width - padL - padR;
  const x = (i: number) =>
    points.length === 1 ? padL + innerW / 2 : padL + (i / (points.length - 1)) * innerW;
  const y = (val: number) => (1 - (val - bottom) / span) * (chartH - 4) + 2;
  const zeroY = y(0);

  const polyline = (key: SeriesKey, variant: Variant, from: number, to: number) => {
    const pts: string[] = [];
    for (let i = from; i <= to; i++) {
      pts.push(`${x(i).toFixed(1)},${y(points[i][key][variant]).toFixed(1)}`);
    }
    return pts.join(' ');
  };

  // Troncon « prevu » : demarre au point REEL de jonction (mois courant) puis suit le
  // budget — relais continu avec la courbe reelle.
  const forecastPolyline = (key: SeriesKey) => {
    if (!hasForecast || lastRealIdx < 0) return '';
    const pts = [`${x(lastRealIdx).toFixed(1)},${y(points[lastRealIdx][key].actual).toFixed(1)}`];
    for (let i = lastRealIdx + 1; i < points.length; i++) {
      pts.push(`${x(i).toFixed(1)},${y(points[i][key].budget).toFixed(1)}`);
    }
    return pts.join(' ');
  };

  // Le graphique n'est PAS accessible au lecteur d'ecran par ses formes : l'aria-label
  // enonce la serie mois par mois (parite PoC).
  const ariaLabel = t('gest:finance.yearChart.aria', {
    year,
    detail: points
      .map((p) => {
        const v = (amounts: { actual: number; budget: number }) =>
          formatCad(p.isForecast ? amounts.budget : amounts.actual, locale);
        const month = formatMonthShort(p.period, locale);
        const suffix = p.isForecast ? ` (${t('gest:finance.yearChart.forecast')})` : '';
        return `${month}${suffix} ${t('gest:finance.series.revenue')} ${v(p.revenue)}, ${t('gest:finance.series.expense')} ${v(p.expense)}, ${t('gest:finance.series.net')} ${v(p.net)}`;
      })
      .join(' ; '),
  });

  const dashLegend = [
    hasForecast ? t('gest:finance.yearChart.forecast') : '',
    showBudget ? t('gest:finance.series.budget') : '',
    showPriorYear ? t('gest:finance.series.priorYear') : '',
  ].filter(Boolean).join(' / ');

  return (
    <section className={CARD}>
      <h2 className="text-head font-semibold text-slate-800 dark:text-slate-100">
        {t('gest:finance.yearChart.title', { year })}
      </h2>
      <p className="mt-1 text-field text-slate-500 dark:text-slate-400">
        {t('gest:finance.yearChart.description')}
      </p>

      <div className="mt-3 flex flex-wrap items-center gap-2">
        <FilterChip
          label={t('gest:finance.series.budget')}
          active={showBudget}
          onClick={() => setShowBudget((v) => !v)}
        />
        <FilterChip
          label={t('gest:finance.series.priorYear')}
          active={showPriorYear}
          onClick={() => setShowPriorYear((v) => !v)}
        />
      </div>

      <div ref={containerRef} className="mt-3 w-full">
        <svg viewBox={`0 0 ${width} ${height}`} width={width} height={height} role="img" aria-label={ariaLabel}>
          {/* Ligne de base a zero (recessive — l'axe ne concurrence jamais la donnee). */}
          <line
            x1={padL} x2={width - padR} y1={zeroY} y2={zeroY}
            strokeWidth={1} className="stroke-slate-200 dark:stroke-slate-700"
          />

          {SERIES.map((s) => (
            <g key={s.key}>
              {showPriorYear ? (
                <polyline
                  points={polyline(s.key, 'priorYear', 0, points.length - 1)}
                  fill="none" stroke={s.color} strokeWidth={1.5} strokeDasharray="2 4"
                  strokeLinecap="round" strokeLinejoin="round" opacity={0.55}
                />
              ) : null}
              {showBudget ? (
                <polyline
                  points={polyline(s.key, 'budget', 0, points.length - 1)}
                  fill="none" stroke={s.color} strokeWidth={1.5} strokeDasharray="5 3"
                  strokeLinecap="round" strokeLinejoin="round" opacity={0.7}
                />
              ) : null}
              {/* Prevu : troncon pointille en relais du reel (mois courant -> dec). */}
              {hasForecast ? (
                <polyline
                  points={forecastPolyline(s.key)}
                  fill="none" stroke={s.color} strokeWidth={1.5} strokeDasharray="5 3"
                  strokeLinecap="round" strokeLinejoin="round" opacity={0.7}
                />
              ) : null}
              {/* Reel : courbe pleine de janvier au mois courant. */}
              {lastRealIdx >= 0 ? (
                <polyline
                  points={polyline(s.key, 'actual', 0, lastRealIdx)}
                  fill="none" stroke={s.color} strokeWidth={2}
                  strokeLinecap="round" strokeLinejoin="round"
                />
              ) : null}
              {points.map((p, i) =>
                i <= lastRealIdx ? (
                  <circle key={p.period} cx={x(i)} cy={y(p[s.key].actual)} r={2.5} fill={s.color} />
                ) : null,
              )}
            </g>
          ))}

          {points.map((p, i) => (
            <text
              key={p.period}
              x={x(i)} y={height - 6}
              textAnchor="middle"
              className="fill-slate-400 text-chip dark:fill-slate-500"
            >
              {formatMonthShort(p.period, locale)}
            </text>
          ))}
        </svg>
      </div>

      {/* Legende : l'identite des series ne repose jamais sur la seule couleur —
          chaque pastille porte son libelle. */}
      <div className="mt-2 flex flex-wrap items-center gap-4 text-meta text-slate-500 dark:text-slate-400">
        {SERIES.map((s) => (
          <span key={s.key} className="flex items-center gap-1.5">
            <span aria-hidden="true" className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: s.color }} />
            {t(s.labelKey)}
          </span>
        ))}
        {dashLegend ? (
          <span className="text-slate-400 dark:text-slate-500">
            {t('gest:finance.yearChart.dashLegend', { kinds: dashLegend })}
          </span>
        ) : null}
      </div>
    </section>
  );
}

/** Chip de bascule (aria-pressed) — standard « chip filtre actif » UI_STANDARDS §4. */
function FilterChip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={`touch-manipulation rounded-full px-3 py-1.5 text-field font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-500/40 ${
        active
          ? 'bg-slate-800 font-semibold text-white dark:bg-slate-600'
          : 'bg-slate-100 text-slate-500 hover:bg-slate-200 hover:text-slate-700 dark:bg-slate-800 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200'
      }`}
    >
      {label}
    </button>
  );
}
